import {
  Badge,
  breakpoints,
  Button,
  Dialog,
  Heading,
  Inline,
  Stack,
  Text,
  textVariants,
  vars,
} from '@etg/wings';
import { Overlay } from '@eti/components';
import { CancellationRefundOfferReason } from '@eti/schema-types';
import { css } from '@eti/styles';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { XCircle } from '@phosphor-icons/react';
import { useSnowplowTracking, useTranslation } from '@eti/providers';
import { price as priceUtil } from '@eti/utils';
import { screenReadersOnlyStyles } from '../../../../../utils/styleUtils';
import CardWithActionButton from '../../common/CardWithActionButton';
import BasicTripInfo from '../../common/BasicTripInfo';
import Dates from '../../common/Dates';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import RouteInfoWithSymbols from '../../common/RouteInfoWithSymbols';
import { getOriginDestinationPerBound } from '../../../utils';
import ExpirationBadge from './ExpirationBadge';
import type { CancellationOptionInfoType } from './models';

const badgeInlineStyles = css`
  flex-wrap: wrap-reverse;
  gap: 8px 16px;
  justify-content: space-between;
`;

interface TopCancellationOptionProps {
  cancellationOption: CancellationOptionInfoType;
  isBestOption: boolean;
  isExpirationDialogOpen: boolean;
  setIsExpirationDialogOpen?: Dispatch<SetStateAction<boolean>>;
}
const TopCancellationOption = ({
  cancellationOption,
  isBestOption,
  isExpirationDialogOpen,
  setIsExpirationDialogOpen,
}: TopCancellationOptionProps) => {
  const { t } = useTranslation();
  const { bounds, cancellationOptionsMutation, journeyType } = useOrderDetails();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleExpirationDismiss = async () => {
    setIsExpirationDialogOpen?.(false);
    await cancellationOptionsMutation();
  };

  const handleOnClick = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Manage Trip',
        action: 'Click',
        label: cancellationOption.reason,
      });
    }
    setIsDrawerOpen(true);
  };

  return (
    <>
      <Stack data-testid="top-cancellation-option">
        <Stack spacing={8}>
          <Inline alignY="center" className={badgeInlineStyles}>
            <Heading level={2}>{cancellationOption.title}</Heading>
            <Inline spacing={8}>
              {isBestOption && (
                <Badge data-testid="best-option-badge">
                  {t('ManageTrip.Cancel.BestOption.Text')}
                </Badge>
              )}
              {cancellationOption.refundOffer !== null && (
                <ExpirationBadge
                  dataTestId="top-cancellation-option-expiration-badge"
                  expires={cancellationOption.refundOffer.expirationTimeInSec}
                />
              )}
            </Inline>
          </Inline>
          {cancellationOption.refundOffer !== null &&
            (cancellationOption.refundOffer.expirationTimeInSec > 0
              ? cancellationOption.refundOffer.refundableAmount && (
                  <>
                    <Text aria-hidden asHtml>
                      {t(
                        'ManageTrip.Cancel.EstimatedRefund.Text',
                        priceUtil.formatPrice(cancellationOption.refundOffer.refundableAmount),
                      )}
                    </Text>
                    <Text asHtml className={screenReadersOnlyStyles}>
                      {t(
                        'ManageTrip.Cancel.EstimatedRefund.Text',
                        priceUtil.replaceWithCurrencyName(
                          priceUtil.formatPrice(cancellationOption.refundOffer.refundableAmount),
                        ),
                      )}
                    </Text>
                  </>
                )
              : cancellationOption.expiredDescription && (
                  <Text>{cancellationOption.expiredDescription}</Text>
                ))}
          {(cancellationOption.refundOffer === null ||
            cancellationOption.refundOffer.expirationTimeInSec > 0) &&
            cancellationOption.descriptionWhenOnTop && (
              <Text>{cancellationOption.descriptionWhenOnTop}</Text>
            )}
        </Stack>
        <CardWithActionButton
          aria-controls="top-cancellation-option-drawer"
          aria-expanded={isDrawerOpen ? 'true' : 'false'}
          aria-haspopup="dialog"
          buttonText={t(
            cancellationOption.reason !== CancellationRefundOfferReason.ScheduleChangeRefund
              ? 'ManageTrip.Cancel.TopOption.Button.Text'
              : 'ManageTrip.RefundRequest.Title',
          )}
          isDisabled={
            cancellationOption.refundOffer !== null &&
            cancellationOption.refundOffer.expirationTimeInSec === 0
          }
          isRedirecting={false}
          onClick={handleOnClick}
        >
          <Stack spacing={4}>
            <Text variant={textVariants._16_BOLD}>
              <RouteInfoWithSymbols
                bounds={getOriginDestinationPerBound(bounds)}
                data-testid="top-cancellation-option"
                journeyType={journeyType}
                spacingY={0}
              />
            </Text>
            {cancellationOption.refundOffer ||
            cancellationOption.reason === CancellationRefundOfferReason.ScheduleChangeRefund ? (
              <BasicTripInfo shouldShowOriginalDates={false} />
            ) : (
              <Dates />
            )}
          </Stack>
        </CardWithActionButton>
      </Stack>
      <Overlay.Drawer
        dataTestId="top-cancellation-option-drawer"
        handleClose={() => setIsDrawerOpen(false)}
        id="top-cancellation-option-drawer"
        isOpen={isDrawerOpen}
      >
        <Overlay.Header
          onClose={() => setIsDrawerOpen(false)}
          title={t(
            cancellationOption.reason !== CancellationRefundOfferReason.ScheduleChangeRefund
              ? 'ManageTrip.Cancel.Drawer.Title'
              : 'ManageTrip.RefundRequest.Title',
          )}
        />
        <Overlay.Content data-testid="top-cancellation-option-drawer-content">
          {cancellationOption.drawerContent}
        </Overlay.Content>
      </Overlay.Drawer>
      {cancellationOption.expiredDialogTitle && cancellationOption.expiredDialogDescription && (
        <Dialog
          closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
          data-testid="top-cancellation-expiration-dialog"
          footer={
            <Inline align="end" collapseBelow={breakpoints._560}>
              <Button onClick={handleExpirationDismiss} variant="primary">
                {t('ManageTrip.Cancel.Expired.Dialog.Button.Text')}
              </Button>
            </Inline>
          }
          illustration={
            <XCircle color={vars.colors.signal.critical.light} size={64} weight="fill" />
          }
          isOpen={isExpirationDialogOpen}
          title={cancellationOption.expiredDialogTitle}
        >
          <Stack spacing={8}>
            <Text>{cancellationOption.expiredDialogDescription}</Text>
          </Stack>
        </Dialog>
      )}
    </>
  );
};

export default TopCancellationOption;
